import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ElementRenderer from './ElementRenderer';
import CodeGenerator from './CodeGenerator/CodeGenerator';
import SystemTooltip from './SystemTooltip';
import Toolbar from './Toolbar';
import { VIRTUAL_SIZE, bboxOf } from '../utils/canvasUtils';
import { canvasToStructurizrDsl } from '../utils/structurizrParser';
import { generateTimewebTerraformFromC4 } from '../utils/c4TerraformGenerator';
import { generateAnsibleFromC4, selectLatestCluster } from '../utils/c4AnsibleGenerator';
import { useTheme } from '../hooks/useTheme';
import { ELEMENT_TYPES, isShape, ElementFactory, createElement } from '../types/elements';
import { listClusters } from '../api/discoveryApi';

// Импорт кастомных хуков
import { useZOrder } from '../hooks/useZOrder';
import { useSelection } from '../hooks/useSelection';
import { useNavigation } from '../hooks/useNavigation';
import { useDrawing } from '../hooks/useDrawing';
import { useBoxSelection } from '../hooks/useBoxSelection';
import { useHistoryAndClipboard } from '../hooks/useHistoryAndClipboard';
import { useCanvasElements } from '../hooks/useCanvasElements';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { WhiteboardContextMenu } from './whiteboard/WhiteboardContextMenu';
import { ArchiMateArrowMarkers } from './ArchiMateArrowMarkers';

import { useDiagramOperations } from '../hooks/useDiagramOperations';
import { parsePlantUml } from '../utils/plantumlParser';
import { parseErdDsl } from '../utils/erdParser';
import { applyErdBoardLayout } from '../utils/erdBoardLayout';
import { useCanvasContextMenu } from '../hooks/useCanvasContextMenu';
import { PropertyPanel } from './whiteboard/components/PropertyPanel';
import { BcmPropertyPanel } from './whiteboard/components/BcmPropertyPanel';
import { useC4Navigation, DRILLABLE_KINDS } from '../hooks/useC4Navigation';
import C4Breadcrumb from './C4Breadcrumb';
import { BcmHeatmapContext } from '../context/BcmHeatmapContext';
import DiagramPublishModal from './DiagramPublishModal';
import DiagramHistoryModal from './DiagramHistoryModal';
import DiagramModal from './DiagramModal';
import { shift } from '../utils/elementUtils';
import { collectPublishLintDiagnostics } from '../utils/publishLintDiagnostics';
import {
    DIAGRAM_DRAFT_STATUS,
    DIAGRAM_EXIT_CONFIRM_MESSAGE,
    buildDiagramDraftKey,
    clearDiagramDraft,
    readDiagramDraft,
    withDraftStatus,
    writeDiagramDraft,
} from '../utils/diagramDrafts';

const Canvas = ({ initialDiagramId = null, mode = 'uml', projectId = null, ToolbarComponent = Toolbar, metamodelContext = null, metamodelId = null, onDiagramLoaded = null, defaultCode = null }) => {
    const { theme, registerElementsUpdate } = useTheme();
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [showCodePanel, setShowCodePanel] = useState(['uml', 'archimate', 'c4', 'erd', 'free'].includes(mode));
    const [showPublishModal, setShowPublishModal] = useState(false);
    const [publishLintDiagnostics, setPublishLintDiagnostics] = useState([]);
    const [publishFitnessWarnings, setPublishFitnessWarnings] = useState([]);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showDiagramModal, setShowDiagramModal] = useState(false);
    const [diagramModalInitialTab, setDiagramModalInitialTab] = useState('save');

    // Флаг пользовательских изменений
    const userModifiedRef = useRef(false);

    // C4 reverse sync
    const c4ElementMapRef = useRef(new Map());
    const registryLintContextRef = useRef({ allowedParticipants: [], allowedNames: [] });
    const [codePanelDsl, setCodePanelDsl] = useState('');

    // Реактивное состояние для перетаскивания (для скрытия точек подключения)
    const [isDragging, setIsDragging] = useState(false);

    // Контекстное меню
    const [ctxMenu, setCtxMenu] = useState({ visible: false, x: 0, y: 0, elementId: null });

    // Tooltip для системы при hover
    const [systemTooltip, setSystemTooltip] = useState({ visible: false, element: null, x: 0, y: 0 });
    const tooltipTimeoutRef = useRef(null);

    // Инициализация хуков
    const { assignZ, ensureZ, syncZCounter, incrementZCounter } = useZOrder();
    const { selectedElementId, selectedElementIds, handleElementSelect, handleElementSelectMultiple, clearSelection, selectAll, setSelectedElementIds } = useSelection();

    // История и буфер обмена
    const { dragInProgressRef, lastActionTypeRef, snapshotBeforeActionRef, recordHistory, snapshotBefore, copyElements, pasteElements, cutElements, undo, redo, canUndo, canRedo, startInteraction, endInteraction, cancelInteraction } = useHistoryAndClipboard();

    // Элементы
    const { elements, setElements, editingTextElement, deleteSelectedElements, handleElementDrag, handleElementDragMultiple, handleElementDoubleClick, handleTextEditComplete, handleTextEditCancel, handleArrowEdit, handleElementResize, handleGenerateElements, addElement, resetMultiDrag } = useCanvasElements(assignZ, incrementZCounter, recordHistory);

    // Ref для актуального списка элементов (защита от stale closure в mouseup)
    const elementsRef = useRef(elements);
    useEffect(() => { elementsRef.current = elements; }, [elements]);
    const codeRef = useRef('');
    const draftDirtyRef = useRef(false);
    const draftKeyRef = useRef(null);
    const draftRecoveryKeyRef = useRef(null);
    const draftRecoveredKeyRef = useRef(null);
    const [loadRecoveryEpoch, setLoadRecoveryEpoch] = useState(0);
    const initialLoadReadyRef = useRef(!initialDiagramId);
    const initialLoadIdentity = `${projectId || ''}:${initialDiagramId || ''}`;
    const initialLoadIdentityRef = useRef(initialLoadIdentity);
    const initialLoadDiagramRef = useRef(initialDiagramId);
    initialLoadDiagramRef.current = initialDiagramId;
    if (initialLoadIdentityRef.current !== initialLoadIdentity) {
        initialLoadIdentityRef.current = initialLoadIdentity;
        initialLoadReadyRef.current = !initialDiagramId;
    }
    const handleDiagramLoaded = useCallback((diagram, loadIdentity = null) => {
        const acknowledgedId = loadIdentity?.diagramId ?? initialDiagramId;
        const acknowledgedProject = loadIdentity?.projectId ?? projectId;
        if (initialDiagramId && acknowledgedId === initialLoadDiagramRef.current && acknowledgedProject === currentProjectIdRef.current) {
            initialLoadReadyRef.current = true;
            setLoadRecoveryEpoch((epoch) => epoch + 1);
        }
        onDiagramLoaded?.(diagram);
    }, [initialDiagramId, onDiagramLoaded, projectId]);
    const handleDiagramLoadFailed = useCallback(({ diagramId, projectId: failedProjectId } = {}) => {
        if (initialDiagramId && diagramId === initialLoadDiagramRef.current && failedProjectId === currentProjectIdRef.current) {
            initialLoadReadyRef.current = true;
            setLoadRecoveryEpoch((epoch) => epoch + 1);
        }
    }, [initialDiagramId]);
    const editRevisionRef = useRef(0);
    const markDiagramModified = useCallback(() => {
        editRevisionRef.current += 1;
        userModifiedRef.current = true;
        draftDirtyRef.current = true;
    }, [userModifiedRef]);

    // Legacy diagrams and parser output may arrive without z values.
    // Normalize them after commit so render stays side-effect free.
    useEffect(() => {
        if (!elements.length) return;

        if (elements.some((el) => el.z == null)) {
            setElements((prev) => ensureZ(prev));
            return;
        }

        syncZCounter(elements);
    }, [elements, ensureZ, setElements, syncZCounter]);

    // Состояние для inline-редактирования C4-элемента через PropertyPanel
    const [c4EditState, setC4EditState] = useState(null);

    // Состояние для inline-редактирования BCM-элемента через BcmPropertyPanel
    const [bcmEditState, setBcmEditState] = useState(null);

    // Heatmap mode for BCM board
    const [bcmHeatmapMode, setBcmHeatmapMode] = useState('none');

    // Обёртка handleTextEditComplete: включает auto-size везде кроме UML-доски
    const handleTextEditCompleteWrapped = useCallback((id, payload) => {
        const currentElement = elements.find((element) => element.id === id);
        const hasChanges = currentElement && Object.entries(payload || {}).some(([key, value]) => currentElement[key] !== value);
        if (!hasChanges) return;
        handleTextEditComplete(id, payload, mode !== 'uml');
        markDiagramModified();
    }, [elements, handleTextEditComplete, markDiagramModified, mode]);

    // Навигация
    const { zoom, spacePressed, setSpacePressed, viewportCenter, zoomIn, zoomOut, fitToBoard, fitBBoxInView, centerToSelectionOrDiagram, startPan, handleWheel, panStateRef, setZoom } = useNavigation(containerRef, elements, selectedElementIds, selectedElementId);

    // Операции с диаграммой (Save, Load, Export)
    const {
        currentDiagramId,
        setCurrentDiagramId,
        currentDiagramMeta,
        setCurrentDiagramMeta,
        handleSaveDiagram,
        handlePublishDiagram,
        handleLoadDiagram,
        handleExportImage,
        isPublishing,
        code,
        setCode,
        diagramGitlabPath,
        setDiagramGitlabPath,
    } = useDiagramOperations({
        initialDiagramId,
        projectId,
        elements,
        setElements,
        clearSelection,
        zoom,
        setZoom,
        containerRef,
        canvasRef,
        userModifiedRef,
        fitBBoxInView,
        theme,
        mode,
        onDiagramLoaded: handleDiagramLoaded,
        onDiagramLoadFailed: handleDiagramLoadFailed,
    });

    // DiagramModal may invoke an onSaved callback from a render that preceded
    // a diagram switch. Keep the identity live so stale callbacks cannot
    // overwrite the currently loaded diagram.
    const currentDiagramIdRef = useRef(currentDiagramId);
    currentDiagramIdRef.current = currentDiagramId;
    const currentProjectIdRef = useRef(projectId);
    currentProjectIdRef.current = projectId;
    const modalSaveContextRef = useRef(null);

    const handleLoadDiagramWithDraftReset = useCallback((...args) => {
        draftDirtyRef.current = false;
        return handleLoadDiagram(...args);
    }, [handleLoadDiagram]);

    useEffect(() => { codeRef.current = code || ''; }, [code]);

    const draftKey = useMemo(() => buildDiagramDraftKey({
        projectId,
        // During the initial async load the hook has not set currentDiagramId
        // yet. Use the requested ID so an existing diagram cannot recover a
        // different project's `new` draft.
        diagramId: currentDiagramId || initialDiagramId,
        mode,
    }), [projectId, currentDiagramId, initialDiagramId, mode]);

    useEffect(() => { draftKeyRef.current = draftKey; }, [draftKey]);

    useEffect(() => {
        if (!draftKey || draftRecoveryKeyRef.current === draftKey) return;
        // Existing diagrams must be accepted from the server first. The
        // recovery pass runs again once the hook publishes the loaded ID.
        if (!initialLoadReadyRef.current) return;
        draftRecoveryKeyRef.current = draftKey;
        const draft = readDiagramDraft(draftKey);
        if (!draft) return;
        draftRecoveredKeyRef.current = draftKey;

        const recoveredCode = draft.code ?? draft.meta?.sourceCode ?? draft.meta?.code;
        if (typeof recoveredCode === 'string') {
            codeRef.current = recoveredCode;
            setCode(recoveredCode);
        }
        if (Array.isArray(draft.elements)) setElements(draft.elements);
        if (draft.meta) setCurrentDiagramMeta(draft.meta);
        markDiagramModified();
    }, [currentDiagramId, draftKey, loadRecoveryEpoch, setCode, setElements, setCurrentDiagramMeta, markDiagramModified]);

    // Wrap handleSaveDiagram to persist metamodelId in diagram meta (free-mode diagrams)
    const handleSaveWithMeta = useCallback(
        (opts = {}) => handleSaveDiagram({ ...opts, extraMeta: { ...(opts.extraMeta || {}), ...(metamodelId != null ? { metamodelId } : {}) } }),
        [handleSaveDiagram, metamodelId]
    );

    const buildDraftPayload = useCallback((nextCode = codeRef.current) => {
        const sourceCodeModes = ['uml', 'archimate', 'c4', 'erd', 'free'];
        const meta = withDraftStatus({
            ...(currentDiagramMeta || {
                name: 'Новая диаграмма',
                domain: 'Architecture Vision',
                version: 1,
                createdAt: new Date().toISOString(),
            }),
            ...(mode ? { type: mode } : {}),
            ...(metamodelId != null ? { metamodelId } : {}),
            code: nextCode || '',
            ...(sourceCodeModes.includes(mode) ? { sourceCode: nextCode || '' } : {}),
            updatedAt: new Date().toISOString(),
        });

        return {
            projectId,
            diagramId: currentDiagramId,
            mode,
            code: nextCode || '',
            meta,
            sync_status: DIAGRAM_DRAFT_STATUS,
            view: {
                zoom,
                scroll: containerRef.current
                    ? { left: containerRef.current.scrollLeft, top: containerRef.current.scrollTop }
                    : { left: 0, top: 0 },
            },
            elements: elementsRef.current,
        };
    }, [projectId, currentDiagramId, mode, currentDiagramMeta, metamodelId, zoom, containerRef]);

    const persistLocalDraft = useCallback((nextCode = codeRef.current) => {
        writeDiagramDraft(draftKeyRef.current, buildDraftPayload(nextCode));
    }, [buildDraftPayload]);

    useEffect(() => {
        if (!userModifiedRef.current) return;
        draftDirtyRef.current = true;
        persistLocalDraft();
    }, [elements, persistLocalDraft]);

    const persistTemporaryDiagram = useCallback(() => (
        Promise.resolve(handleSaveWithMeta({
            silent: true,
            suppressErrorAlert: true,
            preserveDirty: true,
            extraMeta: { sync_status: DIAGRAM_DRAFT_STATUS },
        })).catch((error) => {
            console.warn('[DiagramDraft] backend draft save failed:', error);
        })
    ), [handleSaveWithMeta]);

    const handleUserCodeChange = useCallback((nextCode) => {
        const value = nextCode || '';
        setCode(value);
        codeRef.current = value;
        markDiagramModified();
        persistLocalDraft(value);
    }, [setCode, markDiagramModified, persistLocalDraft]);

    const confirmCanvasExit = useCallback(() => {
        if (!draftDirtyRef.current && !userModifiedRef.current) return true;
        if (!window.confirm(DIAGRAM_EXIT_CONFIRM_MESSAGE)) return false;
        persistLocalDraft();
        if (draftDirtyRef.current) persistTemporaryDiagram();
        return true;
    }, [persistLocalDraft, persistTemporaryDiagram, userModifiedRef]);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (!draftDirtyRef.current && !userModifiedRef.current) return undefined;
            persistLocalDraft();
            if (draftDirtyRef.current) {
                persistTemporaryDiagram();
            }
            event.preventDefault();
            event.returnValue = DIAGRAM_EXIT_CONFIRM_MESSAGE;
            return DIAGRAM_EXIT_CONFIRM_MESSAGE;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [persistLocalDraft, persistTemporaryDiagram, userModifiedRef]);

    const openSaveDiagramModal = useCallback(() => {
        setDiagramModalInitialTab('save');
        setShowDiagramModal(true);
    }, []);

    const openDiagramManager = useCallback(() => {
        setDiagramModalInitialTab('list');
        setShowDiagramModal(true);
    }, []);

    const clearDraftForRevision = useCallback((revision, key = draftKeyRef.current) => {
        if (editRevisionRef.current !== revision) return false;
        if (draftKeyRef.current !== key) return false;
        clearDiagramDraft(key);
        userModifiedRef.current = false;
        draftDirtyRef.current = false;
        return true;
    }, []);

    const handleModalSave = useCallback(async ({ name, domain }) => {
        if (currentProjectIdRef.current !== projectId || draftKeyRef.current !== draftKey ||
            (currentDiagramId && currentDiagramIdRef.current !== currentDiagramId)) return null;
        const revision = editRevisionRef.current;
        const key = draftKey;
        modalSaveContextRef.current = { projectId: currentProjectIdRef.current, diagramId: currentDiagramIdRef.current, key };
        const saved = await handleSaveWithMeta({ silent: true, preserveDirty: true, metaOverride: { name, domain } });
        clearDraftForRevision(revision, key);
        return saved;
    }, [handleSaveWithMeta, clearDraftForRevision, projectId, draftKey, currentDiagramId]);

    const handleModalSaved = useCallback((saved) => {
        if (!saved) return;
        if (currentProjectIdRef.current !== projectId || draftKeyRef.current !== draftKey ||
            (currentDiagramId && currentDiagramIdRef.current !== currentDiagramId)) return;
        const context = modalSaveContextRef.current;
        if (context && (context.projectId !== currentProjectIdRef.current || context.key !== draftKeyRef.current || (context.diagramId && context.diagramId !== currentDiagramIdRef.current))) return;
        if (saved.id && currentDiagramIdRef.current && saved.id !== currentDiagramIdRef.current) return;
        setCurrentDiagramId(saved.id);
        setCurrentDiagramMeta(saved.meta || null);
        setDiagramGitlabPath(saved.gitlab_path || diagramGitlabPath || null);
    }, [setCurrentDiagramId, setCurrentDiagramMeta, setDiagramGitlabPath, diagramGitlabPath, projectId, draftKey, currentDiagramId]);

    const openPublishModalWithLintPreflight = useCallback(async () => {
        const revision = editRevisionRef.current;
        await handleSaveWithMeta({ silent: true, preserveDirty: true });
        clearDraftForRevision(revision);
        setPublishLintDiagnostics(collectPublishLintDiagnostics({
            mode,
            code,
            metamodelContext,
            allowedParticipants: registryLintContextRef.current.allowedParticipants,
            allowedNames: registryLintContextRef.current.allowedNames,
        }));
        setPublishFitnessWarnings([]);
        setShowPublishModal(true);
    }, [handleSaveWithMeta, clearDraftForRevision, mode, code, metamodelContext]);

    const handleRegistryContextChange = useCallback((context) => {
        registryLintContextRef.current = {
            allowedParticipants: Array.isArray(context?.allowedParticipants) ? context.allowedParticipants : [],
            allowedNames: Array.isArray(context?.allowedNames) ? context.allowedNames : [],
        };
    }, []);

    const handlePublishModalConfirm = useCallback(async (selectedEntry) => {
        const shouldOverrideFitness = publishFitnessWarnings.length > 0;
        const revision = editRevisionRef.current;
        setPublishLintDiagnostics([]);
        try {
            await handlePublishDiagram(selectedEntry, { fitnessOverride: shouldOverrideFitness });
            if (!clearDraftForRevision(revision)) {
                userModifiedRef.current = true;
                draftDirtyRef.current = true;
            }
            setPublishFitnessWarnings([]);
            setShowPublishModal(false);
        } catch (error) {
            if (error?.isFitnessWarnings) {
                setPublishFitnessWarnings(error.warnings || []);
                setShowPublishModal(true);
                return;
            }
            setShowPublishModal(false);
        }
    }, [handlePublishDiagram, publishFitnessWarnings, clearDraftForRevision]);

    // UML diagrams: re-derive canvas elements from code whenever a diagram is loaded.
    // Code is the source of truth for PlantUML; stored elements can be stale if the
    // user edited the code panel but did not explicitly click "Generate" before saving.
    // Keyed on currentDiagramId so it fires once per load, not on every keystroke.
    useEffect(() => {
        if (mode !== 'uml' || !code || !currentDiagramId) return;
        if (draftRecoveredKeyRef.current === draftKey) return;
        try {
            const derived = parsePlantUml(code, ElementFactory);
            if (derived && derived.length > 0) {
                const derivedBBox = bboxOf(derived);
                let placed = derived;

                if (derivedBBox) {
                    const cx = (derivedBBox.minX + derivedBBox.maxX) / 2;
                    const cy = (derivedBBox.minY + derivedBBox.maxY) / 2;
                    const dx = VIRTUAL_SIZE / 2 - cx;
                    const dy = VIRTUAL_SIZE / 2 - cy;
                    placed = shift(derived, dx, dy);
                }

                setElements(placed);

                const placedBBox = bboxOf(placed);
                if (placedBBox) {
                    fitBBoxInView(placedBBox);
                }
            }
        } catch (e) {
            console.warn('[Canvas] UML re-derive failed:', e);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDiagramId]); // intentionally omit code/setElements — only re-derive on diagram load

    // ERD diagrams: re-derive canvas elements from code on load, applying stored layout positions.
    useEffect(() => {
        if (mode !== 'erd' || !code || !currentDiagramId) return;
        if (draftRecoveredKeyRef.current === draftKey) return;
        try {
            const derived = parseErdDsl(code, ElementFactory);
            if (!derived || derived.length === 0) return;
            const layout = currentDiagramMeta?.erdLayout ?? {};
            const laid = derived.map((el) => {
                if (!el._semanticId) return el;
                const pos = layout[el._semanticId];
                return pos ? { ...el, x: pos.x, y: pos.y } : el;
            });
            const routed = applyErdBoardLayout(laid);
            const bb = bboxOf(routed);
            let placed = routed;
            if (bb) {
                const cx = (bb.minX + bb.maxX) / 2;
                const cy = (bb.minY + bb.maxY) / 2;
                placed = shift(routed, VIRTUAL_SIZE / 2 - cx, VIRTUAL_SIZE / 2 - cy);
            }
            setElements(placed);
            const placedBb = bboxOf(placed);
            if (placedBb) fitBBoxInView(placedBb);
        } catch (e) {
            console.warn('[Canvas] ERD re-derive failed:', e);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDiagramId]); // only re-derive on diagram load, not on every keystroke

    // Обёртки для операций (вызывают userModified)
    const wrappedDeleteSelectedElements = useCallback(() => {
        const hasSelection = selectedElementIds.size > 0 || Boolean(selectedElementId);
        if (!hasSelection) return;
        deleteSelectedElements(selectedElementIds, selectedElementId, clearSelection, () => snapshotBefore(elements));
        markDiagramModified();
    }, [deleteSelectedElements, selectedElementIds, selectedElementId, clearSelection, snapshotBefore, elements, markDiagramModified]);

    const wrappedSelectAll = useCallback(() => { setElements(selectAll(elements)); }, [setElements, selectAll, elements]);
    const wrappedCopyElements = useCallback(() => { copyElements(elements, selectedElementIds, selectedElementId); }, [copyElements, elements, selectedElementIds, selectedElementId]);
    const wrappedPasteElements = useCallback(() => {
        const pasted = pasteElements();
        if (pasted && pasted.length > 0) markDiagramModified();
        return pasted;
    }, [pasteElements, markDiagramModified]);
    const wrappedCutElements = useCallback(() => { cutElements(elements, selectedElementIds, selectedElementId, wrappedDeleteSelectedElements); }, [cutElements, elements, selectedElementIds, selectedElementId, wrappedDeleteSelectedElements]);
    const wrappedCenterToSelection = useCallback(() => { centerToSelectionOrDiagram(elements, selectedElementIds, selectedElementId); }, [centerToSelectionOrDiagram, elements, selectedElementIds, selectedElementId]);

    // C4 context navigation (only active in c4 mode)
    const c4NavigationEnabled = mode === 'c4';
    const { contextStack, currentContext, canDrillDown, drillDown, goBack, jumpTo, filterForContext, tagWithContext } = useC4Navigation();

    // Контекстное меню (пункты)
    const { contextMenuItems, handleUpdateElement } = useCanvasContextMenu({
        elements,
        setElements,
        ctxMenu,
        userModifiedRef,
        onDiagramModified: markDiagramModified,
        wrappedDeleteSelectedElements,
        projectId,
    });

    // Prepend drill-down item to context menu when in c4 mode and element is drillable
    const finalContextMenuItems = useMemo(() => {
        if (!c4NavigationEnabled || !ctxMenu.element || !canDrillDown(ctxMenu.element)) {
            return contextMenuItems;
        }
        const el = ctxMenu.element;
        const viewLabel = DRILLABLE_KINDS[el.c4.kind];
        return [
            { label: `Open ${viewLabel}`, action: () => drillDown(el, elements) },
            { label: 'divider' },
            ...contextMenuItems,
        ];
    }, [c4NavigationEnabled, ctxMenu.element, canDrillDown, contextMenuItems, drillDown, elements]);

    // Добавляем эффект для закрытия меню при скролле (wheel)
    const handleWheelWrapped = useCallback((e) => {
         if (ctxMenu.visible) closeContextMenu();
         handleWheel(e);
    }, [handleWheel, ctxMenu.visible]);

    // Открывает PropertyPanel для редактирования текста C4-элемента
    const openC4PropertyPanel = useCallback((parentId, parentElement) => {
        const textEl = elements.find((el) => el.type === 'text' && el.attachedToElement === parentId);
        if (!textEl) {
            // Fallback: нет текстового элемента — создаём через стандартный flow
            handleElementDoubleClick(parentId);
            return;
        }

        const container = containerRef.current;
        const containerRect = container?.getBoundingClientRect() || { left: 0, top: 0 };
        const scrollLeft = container?.scrollLeft || 0;
        const scrollTop = container?.scrollTop || 0;

        // Позиционируем панель под C4-блоком, центрируем по горизонтали
        const elemBottom = (parentElement.y + parentElement.height) * zoom;
        const elemCenterX = (parentElement.x + parentElement.width / 2) * zoom;
        const panelW = 292; // approx panel width

        const screenX = containerRect.left + elemCenterX - panelW / 2 - scrollLeft;
        const screenY = containerRect.top + elemBottom - scrollTop + 8;

        const lines = (textEl.content || '').split('\n');
        const lineLabels = ['Name', 'Type / Technology', 'Description'];
        const linePlaceholders = ['Enter name…', 'Enter type or technology…', 'Enter description…'];

        setC4EditState({
            textId: textEl.id,
            parentId,
            lines: [...lines],
            x: Math.max(8, screenX),
            y: Math.max(8, screenY),
            lineLabels,
            linePlaceholders,
        });
    }, [elements, containerRef, zoom, handleElementDoubleClick]);

    // Opens PropertyPanel for editing a BCM element
    const openBcmPropertyPanel = useCallback((id, element) => {
        const container = containerRef.current;
        const containerRect = container?.getBoundingClientRect() || { left: 0, top: 0 };
        const scrollLeft = container?.scrollLeft || 0;
        const scrollTop = container?.scrollTop || 0;

        const elemBottom = (element.y + element.height) * zoom;
        const elemCenterX = (element.x + element.width / 2) * zoom;
        const panelW = 300;

        const screenX = containerRect.left + elemCenterX - panelW / 2 - scrollLeft;
        const screenY = containerRect.top + elemBottom - scrollTop + 8;

        setBcmEditState({
            elementId: id,
            name: element.content || '',
            level: element.bcm?.level || 'capability',
            owner: element.bcm?.owner || '',
            maturity: element.bcm?.maturity ?? 0,
            status: element.bcm?.status || 'active',
            x: Math.max(8, screenX),
            y: Math.max(8, screenY),
        });
    }, [containerRef, zoom]);

    // Saves changes from BCM PropertyPanel back to the element
    const handleBcmSave = useCallback((id, updates) => {
        const currentElement = elements.find((element) => element.id === id);
        const currentBcm = currentElement?.bcm || {};
        const hasChanges = currentElement && Object.entries(updates || {}).some(([key, value]) => (
            key === 'name' ? currentElement.content !== value : currentBcm[key] !== value
        ));
        if (!hasChanges) return;
        setElements(prev => prev.map(el => {
            if (el.id !== id) return el;
            const next = { ...el };
            if (updates.name !== undefined) next.content = updates.name;
            if (updates.owner !== undefined || updates.maturity !== undefined || updates.status !== undefined) {
                next.bcm = {
                    ...el.bcm,
                    ...(updates.owner !== undefined && { owner: updates.owner }),
                    ...(updates.maturity !== undefined && { maturity: updates.maturity }),
                    ...(updates.status !== undefined && { status: updates.status }),
                };
            }
            return next;
        }));
        markDiagramModified();
        setBcmEditState(prev => prev ? { ...prev, ...updates } : prev);
    }, [elements, setElements, markDiagramModified]);

    const handleCreateConnectedElement = useCallback((sourceId, side, point, shapeType) => {
        const source = elements.find(el => el.id === sourceId);
        if (!source) return;
        const w = source.width || 120;
        const h = source.height || 60;
        const newX = point.x + 160;
        const newY = point.y - h / 2;

        let newEl;
        if (shapeType === 'circle') {
            newEl = assignZ(ElementFactory.circle(newX, newY, Math.min(w, h) / 2));
        } else if (typeof ElementFactory[shapeType] === 'function') {
            newEl = assignZ(ElementFactory[shapeType](newX, newY));
        } else {
            newEl = assignZ(ElementFactory.rectangle(newX, newY, w, h));
        }
        incrementZCounter();

        const destY = newEl.radius ? newY + newEl.radius : newY + (newEl.height || h) / 2;
        const arrow = assignZ(ElementFactory.arrow(point.x, point.y, newX, destY));
        arrow.startConnection = { elementId: sourceId, side };
        arrow.endConnection = { elementId: newEl.id, side: 'left' };
        incrementZCounter();

        addElement([newEl, arrow]);
        recordHistory(elementsRef.current, 'Создание связанного элемента');
        markDiagramModified();
    }, [elements, assignZ, incrementZCounter, addElement, recordHistory, markDiagramModified]);

    const handleConnectorDoubleClick = useCallback((sourceId, side, point) => {
        const source = elements.find(el => el.id === sourceId);
        if (!source) return;

        const r = source.radius;
        const w = source.width || 120;
        const h = source.height || 60;
        const newX = point.x + 160;
        const newY = r ? point.y - r : point.y - h / 2;

        // Clone source element at new position with a fresh id
        const { id: _id, x: _x, y: _y, z: _z, selected: _sel,
                startConnection: _sc, endConnection: _ec, pathPoints: _pp,
                ...visualProps } = source;
        const newEl = assignZ(createElement(source.type, newX, newY, visualProps));
        incrementZCounter();

        const destY = newEl.radius ? newY + newEl.radius : newY + (newEl.height || h) / 2;
        const arrow = assignZ(ElementFactory.arrow(point.x, point.y, newX, destY));
        arrow.startConnection = { elementId: sourceId, side };
        arrow.endConnection = { elementId: newEl.id, side: 'left' };
        incrementZCounter();

        addElement([newEl, arrow]);
        recordHistory(elementsRef.current, 'Создание связанного элемента');
        markDiagramModified();
    }, [elements, assignZ, incrementZCounter, addElement, recordHistory, markDiagramModified]);

    // Двойной клик: C4-элементы → PropertyPanel, остальные — стандартное поведение
    const handleElementDoubleClickWrapped = useCallback((id) => {
        const element = elements.find(el => el.id === id);
        if (!element) return;

        // Не открываем повторно если уже редактируем этот элемент
        if (c4EditState?.parentId === id || c4EditState?.textId === id) return;

        // BCM element: open BCM PropertyPanel
        if (element.type === 'rectangle' && element.bcm?.level) {
            if (bcmEditState?.elementId === id) return; // already editing this element
            openBcmPropertyPanel(id, element);
            return;
        }

        // C4-прямоугольник: открываем PropertyPanel
        if (element.type === 'rectangle' && element.c4?.kind) {
            openC4PropertyPanel(id, element);
            return;
        }

        // Текстовый элемент, прикреплённый к C4: открываем PropertyPanel родителя
        if (element.type === 'text' && element.attachedToElement) {
            const parent = elements.find(el => el.id === element.attachedToElement);
            if (parent?.c4?.kind) {
                if (c4EditState?.parentId === parent.id) return;
                openC4PropertyPanel(parent.id, parent);
                return;
            }
        }

        // Обычные фигуры (не C4): двойной клик не триггерит редактирование
        if (element.type === 'arrow' || element.type === 'rectangle' || element.type === 'circle') {
            return;
        }

        // Текстовые элементы и прочие типы — стандартное редактирование
        handleElementDoubleClick(id);
    }, [elements, handleElementDoubleClick, c4EditState, openC4PropertyPanel, bcmEditState, openBcmPropertyPanel]);

    const { tool, setTool, previewElement, isCreatingArrowFromConnection, handleCanvasMouseDown, handleCanvasMouseMove, finalizePreview, handleStartArrowFromConnection, handleArrowFromConnectionMouseMove, handleArrowFromConnectionMouseUp } = useDrawing(canvasRef, zoom, elements, assignZ, incrementZCounter);
    const { isSelecting, selectionBox, handleSelectionStart, handleSelectionMove, handleSelectionEnd } = useBoxSelection(canvasRef, zoom);

    // Обёртка генерации элементов: помечаем, что пользователь внёс изменения.
    // Для ERD: применяем сохранённый layout (позиции) перед рендерингом.
    const handleGenerateElementsWrapped = useCallback((generated) => {
        if (!Array.isArray(generated) || generated.length === 0) return;
        markDiagramModified();
        let next = generated;
        if (mode === 'erd' && currentDiagramMeta?.erdLayout) {
            const layout = currentDiagramMeta.erdLayout;
            next = generated.map((el) => {
                if (!el._semanticId) return el;
                const pos = layout[el._semanticId];
                return pos ? { ...el, x: pos.x, y: pos.y } : el;
            });
        }

        if (mode === 'erd') {
            const routed = applyErdBoardLayout(next);
            handleGenerateElements(routed);
            const bb = bboxOf(routed);
            if (bb) {
                setTimeout(() => fitBBoxInView(bb), 100);
            }
        } else {
            handleGenerateElements(next);
        }
    }, [handleGenerateElements, mode, currentDiagramMeta, fitBBoxInView, markDiagramModified]);

    // Обработчики undo/redo
    const handleUndo = useCallback(() => {
        const previousState = undo();
        if (previousState) {
            setElements(previousState);
            clearSelection();
            markDiagramModified();
        }
    }, [undo, setElements, clearSelection, markDiagramModified]);

    const handleRedo = useCallback(() => {
        const nextState = redo();
        if (nextState) {
            setElements(nextState);
            clearSelection();
            markDiagramModified();
        }
    }, [redo, setElements, clearSelection, markDiagramModified]);

    // Обновление цветов по теме
    useEffect(() => { registerElementsUpdate(setElements); }, [registerElementsUpdate, setElements]);

    // Клавиатурные шорткаты
    useKeyboardShortcuts({
        deleteSelectedElements: wrappedDeleteSelectedElements,
        selectAll: wrappedSelectAll,
        copyElements: wrappedCopyElements,
        pasteElements: wrappedPasteElements,
        cutElements: wrappedCutElements,
        elements,
        selectedElementIds,
        selectedElementId,
        setShowCodePanel,
        recordHistory,
        setElements,
        setTool, // проброс нового
        onSaveDiagram: openSaveDiagramModal,
        onUndo: handleUndo,
        onRedo: handleRedo
    });

    // Навигация Space
    useEffect(() => {
        const kd = (e) => {
            if (e.code === 'Space' && !e.target.matches('input, textarea, [contenteditable]')) {
                e.preventDefault();
                setSpacePressed(true);
            }
        };
        const ku = () => {
            setSpacePressed(false);
            panStateRef.current.panning = false;
        };
        window.addEventListener('keydown', kd, { passive: false });
        window.addEventListener('keyup', ku);
        return () => {
            window.removeEventListener('keydown', kd);
            window.removeEventListener('keyup', ku);
        };
    }, [setSpacePressed, panStateRef]);

    // Обработчики мыши (down, move, up)
    const handleMouseDown = (e) => {
        startInteraction();
        if (ctxMenu.visible) {
             closeContextMenu();
        }

        if (spacePressed) { startPan(e); return; }
        if (tool === 'select') {
            // Очищаем выделение и начинаем рамку только при клике по самом холсту
            if (e.target !== e.currentTarget) return;
            if (!e.ctrlKey && !e.metaKey) setElements(clearSelection());
            handleSelectionStart(e);
            return;
        }
        if (isCreatingArrowFromConnection) return;
        const r = handleCanvasMouseDown(e, recordHistory);
        if (r?.addElement) {
            const toAdd = c4NavigationEnabled ? tagWithContext(r.addElement, currentContext.elementId) : r.addElement;
            addElement(toAdd);
            markDiagramModified();
        }

        // Закрываем меню при начале любого действия
        if (ctxMenu.visible) closeContextMenu();
    };
    const handleMouseMove = (e) => {
        if (isCreatingArrowFromConnection) { handleArrowFromConnectionMouseMove(e); return; }
        if (isSelecting) { handleSelectionMove(e); return; }
        handleCanvasMouseMove(e);
    };
    const handleMouseUp = () => {
        if (isCreatingArrowFromConnection) {
            const arrow = handleArrowFromConnectionMouseUp(recordHistory);
            if (arrow) {
                const tagged = c4NavigationEnabled ? tagWithContext(arrow, currentContext.elementId) : arrow;
                addElement(tagged);
                markDiagramModified();
                recordHistory(elementsRef.current, 'Новая стрелка');
            }
        } else if (isSelecting) {
            const { selectedIds } = handleSelectionEnd(elements);
            setSelectedElementIds(selectedIds);
            setElements((p) => p.map((el) => ({ ...el, selected: selectedIds.has(el.id) })));
        } else {
            const el = finalizePreview(recordHistory);
            if (el) {
                const tagged = c4NavigationEnabled ? tagWithContext(el, currentContext.elementId) : el;
                // Поддержка массива элементов (например, C4: блок + подпись)
                if (Array.isArray(tagged)) {
                    addElement(tagged);
                    markDiagramModified();
                    const first = tagged[0];
                    if (first && first.id) {
                        const sel = new Set([first.id]);
                        setSelectedElementIds(sel);
                        setElements((prev) => prev.map((x) => ({ ...x, selected: x.id === first.id })));
                    }
                } else {
                    addElement(tagged);
                    markDiagramModified();
                    // Автовыделение нового элемента для показа точек подключения
                    const sel = new Set([tagged.id]);
                    setSelectedElementIds(sel);
                    setElements((prev) => prev.map((x) => ({ ...x, selected: x.id === tagged.id })));
                }
            }
        }
        // Commit one history entry for any drag gesture that just finished
        if (dragInProgressRef.current) {
            endInteraction(elementsRef.current, 'Изменение');
        } else {
            cancelInteraction();
        }
        // Завершаем жесты
        dragInProgressRef.current = false;
        setIsDragging(false);
        resetMultiDrag();
    };

    // Глобальный сброс флагов при отпускании мыши вне SVG
    useEffect(() => {
        const up = () => {
            if (dragInProgressRef.current) {
                endInteraction(elementsRef.current, 'Изменение');
            } else {
                cancelInteraction();
            }
            dragInProgressRef.current = false;
            setIsDragging(false);
        };
        window.addEventListener('mouseup', up);
        return () => window.removeEventListener('mouseup', up);
    }, [dragInProgressRef, endInteraction, cancelInteraction]);

    // Контекстное меню
    const openContextMenu = (e, element) => {
        const rect = containerRef.current?.getBoundingClientRect();
        setCtxMenu({ visible: true, x: e.clientX - (rect?.left || 0), y: e.clientY - (rect?.top || 0), elementId: element.id, element });
        const updateElements = handleElementSelect(element.id, true);
        setElements(updateElements);
    };
    const closeContextMenu = () => setCtxMenu((v) => ({ ...v, visible: false }));

    const handleHoverStart = (element, x, y) => {
        // Немедленно показываем tooltip
        const rect = containerRef.current?.getBoundingClientRect();
        console.debug('[Canvas] hover start for element:', element);
        setSystemTooltip({
            visible: true,
            element,
            x: x - (rect?.left || 0),
            y: y - (rect?.top || 0)
        });
    };

    const handleHoverEnd = () => {
        setSystemTooltip({ visible: false, element: null, x: 0, y: 0 });
    };

    // Очистка при размонтировании
    useEffect(() => {
        const timeoutId = tooltipTimeoutRef.current;
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, []);

    const handleC4SyncToCode = useCallback(() => {
        if (mode !== 'c4') return;
        const dsl = canvasToStructurizrDsl(elements, c4ElementMapRef.current, currentDiagramMeta?.name || 'Workspace');
        if (!dsl) return;
        setCodePanelDsl(dsl);
        setShowCodePanel(true);
    }, [elements, mode, currentDiagramMeta]);

    const handleC4ExportTerraform = useCallback(() => {
        if (mode !== 'c4') return;

        const terraform = generateTimewebTerraformFromC4(elements, currentDiagramMeta?.name || 'architecture');
        const blob = new Blob([terraform], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'main.tf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [elements, mode, currentDiagramMeta]);

    const downloadTextFile = useCallback((content, filename) => {
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, []);

    const handleC4ExportAnsible = useCallback(async () => {
        if (mode !== 'c4') return;

        let clusters = [];
        try {
            clusters = projectId ? await listClusters(projectId) : [];
        } catch (error) {
            alert(`Не удалось получить список Kubernetes-кластеров: ${error?.message || 'unknown error'}`);
            return;
        }
        const cluster = selectLatestCluster(clusters || []);
        if (!cluster) {
            alert('В пространстве нет подключенного Kubernetes-кластера для Ansible export');
            return;
        }

        const files = generateAnsibleFromC4(elements, currentDiagramMeta?.name || 'Atlas', cluster);
        downloadTextFile(files.playbook, 'playbook.yml');
        downloadTextFile(files.inventory, 'inventory.ini');
    }, [downloadTextFile, elements, mode, currentDiagramMeta, projectId]);

    useEffect(() => {
        if (codePanelDsl) setCodePanelDsl('');
    }, [codePanelDsl]);

    // Сортировка элементов по Z
    const sortedElements = ensureZ(elements).slice().sort((a, b) => (a.z || 0) - (b.z || 0));

    // In c4 mode, only show elements belonging to the current context level
    const visibleElements = c4NavigationEnabled
        ? filterForContext(sortedElements, currentContext)
        : sortedElements;

    return (
        <BcmHeatmapContext.Provider value={bcmHeatmapMode}>
        <div className="canvas-container" style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden', background: theme.background }}>
            <ToolbarComponent
                tool={tool}
                setTool={setTool}
                zoomIn={zoomIn}
                zoomOut={zoomOut}
                fitToBoard={() => {
                    const bb = elements.length > 0 ? bboxOf(elements) : null;
                    if (bb) fitBBoxInView(bb); else fitToBoard();
                }}
                centerToSelection={wrappedCenterToSelection}
                showCodePanel={showCodePanel}
                setShowCodePanel={(v) => ['uml', 'archimate', 'c4', 'erd', 'free'].includes(mode) && setShowCodePanel(v)}
                elements={elements}
                onLoadDiagram={handleLoadDiagramWithDraftReset}
                currentDiagramId={currentDiagramId}
                zoom={zoom}
                scrollPosition={{ left: 0, top: 0 }}
                currentDiagramMeta={currentDiagramMeta}
                topOffset={0}
                onExportImage={handleExportImage}
                onUndo={handleUndo}
                onRedo={handleRedo}
                canUndo={canUndo}
                canRedo={canRedo}
                code={code}
                setCode={setCode}
                onSyncToCode={mode === 'c4' ? handleC4SyncToCode : undefined}
                onExportTerraform={mode === 'c4' ? handleC4ExportTerraform : undefined}
                onExportAnsible={mode === 'c4' ? handleC4ExportAnsible : undefined}
                heatmapMode={bcmHeatmapMode}
                onHeatmapChange={mode === 'bcm' ? setBcmHeatmapMode : undefined}
                onSaveDiagram={openSaveDiagramModal}
                onOpenDiagramManager={openDiagramManager}
                onPublishDiagram={openPublishModalWithLintPreflight}
                isPublishing={isPublishing}
                diagramGitlabPath={diagramGitlabPath}
                onShowHistory={diagramGitlabPath ? () => setShowHistoryModal(true) : undefined}
                onBeforeNavigate={confirmCanvasExit}
            />

            {showDiagramModal && (
                <DiagramModal
                    visible={showDiagramModal}
                    onClose={() => setShowDiagramModal(false)}
                    elements={elements}
                    onLoadDiagram={handleLoadDiagramWithDraftReset}
                    currentDiagramId={currentDiagramId}
                    zoom={zoom}
                    scrollPosition={containerRef.current ? { left: containerRef.current.scrollLeft, top: containerRef.current.scrollTop } : { left: 0, top: 0 }}
                    currentDiagramMeta={currentDiagramMeta}
                    initialTab={diagramModalInitialTab}
                    code={code}
                    setCode={setCode}
                    defaultDiagramType={mode}
                    fixedDiagramType={mode}
                    extraMeta={metamodelId != null ? { metamodelId } : null}
                    onSave={handleModalSave}
                    onSaved={handleModalSaved}
                    projectId={projectId}
                />
            )}

            {/* History modal */}
            {showHistoryModal && currentDiagramId && (
                <DiagramHistoryModal
                    projectId={projectId}
                    diagramId={currentDiagramId}
                    diagramName={currentDiagramMeta?.name || 'Диаграмма'}
                    onClose={() => setShowHistoryModal(false)}
                />
            )}

            {/* Publish modal */}
            <DiagramPublishModal
                isOpen={showPublishModal}
                onClose={() => {
                    setPublishLintDiagnostics([]);
                    setPublishFitnessWarnings([]);
                    setShowPublishModal(false);
                }}
                diagramId={currentDiagramId}
                projectId={projectId}
                isPublishing={isPublishing}
                lintDiagnostics={publishLintDiagnostics}
                fitnessWarnings={publishFitnessWarnings}
                onConfirm={handlePublishModalConfirm}
            />

            {/* C4 breadcrumb navigation */}
            {c4NavigationEnabled && (
                <C4Breadcrumb
                    contextStack={contextStack}
                    onJumpTo={jumpTo}
                    onGoBack={goBack}
                />
            )}

            {/* Прокручиваемая доска */}
            <div ref={containerRef} onWheel={handleWheelWrapped} style={{ position: 'absolute', top: 56, left: 0, right: 0, bottom: 0, overflow: 'auto', cursor: spacePressed ? 'grab' : 'default' }}>
                <div style={{ width: VIRTUAL_SIZE * zoom, height: VIRTUAL_SIZE * zoom, position: 'relative' }}>
                    <svg ref={canvasRef} width={VIRTUAL_SIZE} height={VIRTUAL_SIZE} style={{ position: 'absolute', left: 0, top: 0, transform: `scale(${zoom})`, transformOrigin: 'top left', background: theme.canvasBackground }} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
                        <defs>
                            <ArchiMateArrowMarkers />
                        </defs>
                        {visibleElements.map((el) => (
                            <ElementRenderer
                                key={el.id}
                                element={el}
                                onSelect={(id, keep) => { const updateElements = handleElementSelect(id, keep); setElements(updateElements); }}
                                onDrag={(id, x, y) => { setIsDragging(true); markDiagramModified(); handleElementDrag(id, x, y, dragInProgressRef, snapshotBeforeActionRef, lastActionTypeRef); }}
                                onArrowEdit={(arrowId, isStart, x, y) => { markDiagramModified(); handleArrowEdit(arrowId, isStart, x, y, dragInProgressRef, snapshotBeforeActionRef, lastActionTypeRef); }}
                                onStartArrowFromConnection={handleStartArrowFromConnection}
                                showConnectionPoints={true}
                                // Изменяем обработчик двойного клика - используем обертку
                                onDoubleClick={handleElementDoubleClickWrapped}
                                onSelectMultiple={(id, ev) => { const updateElements = handleElementSelectMultiple(id, ev); setElements(updateElements); }}
                                onDragMultiple={(id, x, y) => { setIsDragging(true); handleElementDragMultiple(id, x, y, selectedElementIds, dragInProgressRef, snapshotBeforeActionRef, lastActionTypeRef); if (dragInProgressRef.current) markDiagramModified(); }}
                                editingTextElement={editingTextElement}
                                onTextEditComplete={handleTextEditCompleteWrapped}
                                onTextEditCancel={handleTextEditCancel}
                                onArrowCapsChange={(id, s, e) => { markDiagramModified(); setElements((prev) => prev.map((x) => x.id === id ? { ...x, startCap: s, endCap: e } : x)); }}
                                zoom={zoom}
                                onContextMenu={openContextMenu}
                                onArrowPathPointDrag={(id, idx, x, y) => { markDiagramModified(); setElements((prev) => prev.map((a) => { if (a.id !== id) return a; const base = a.pathPoints && a.pathPoints.length ? a.pathPoints : [{ x: a.x, y: a.y }, { x: a.x2, y: a.y2 }]; const newPts = base.map((p, i) => i === idx ? { x, y } : p); return { ...a, pathPoints: newPts }; })); }}
                                onArrowAddPoint={(id, x, y, after = 0) => { markDiagramModified(); setElements((prev) => prev.map((a) => { if (a.id !== id) return a; const pts = a.pathPoints && a.pathPoints.length ? JSON.parse(JSON.stringify(a.pathPoints)) : [{ x: a.x, y: a.y }, { x: a.x2, y: a.y2 }]; const ins = Math.max(0, Math.min(after + 1, pts.length - 1)); pts.splice(ins, 0, { x, y }); return { ...a, pathPoints: pts }; })); }}
                                onResizeStart={() => { dragInProgressRef.current = true; setIsDragging(true); if (!snapshotBeforeActionRef.current) snapshotBefore(); lastActionTypeRef.current = 'resize'; }}
                                onResize={(id, props) => { markDiagramModified(); handleElementResize(id, props); }}
                                isDraggingElement={isDragging}
                                onCreateConnectedElement={handleCreateConnectedElement}
                                onConnectorDoubleClick={handleConnectorDoubleClick}
                            />
                        ))}

                        {/* C4 drill-down indicators: pill badge centered at bottom of drillable elements */}
                        {c4NavigationEnabled && !isDragging && visibleElements
                            .filter((el) => el.type === 'rectangle' && canDrillDown(el))
                            .map((el) => {
                                const viewLabel = DRILLABLE_KINDS[el.c4?.kind] || 'View';
                                const pillW = Math.min(el.width - 16, 130);
                                const pillH = 20;
                                const pillX = el.x + el.width / 2 - pillW / 2;
                                const pillY = el.y + el.height - pillH - 8;
                                return (
                                    <g
                                        key={`drill-${el.id}`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={(e) => { e.stopPropagation(); drillDown(el, elements); }}
                                    >
                                        {/* Wider hit area */}
                                        <rect x={pillX - 4} y={pillY - 4} width={pillW + 8} height={pillH + 8}
                                            fill="transparent" />
                                        {/* Pill background */}
                                        <rect x={pillX} y={pillY} width={pillW} height={pillH} rx={10}
                                            fill="rgba(0,0,0,0.45)" stroke="rgba(255,255,255,0.55)" strokeWidth={1} />
                                        <text x={pillX + pillW / 2} y={pillY + pillH / 2 + 4}
                                            textAnchor="middle" fill="white" fontSize={10} fontWeight={500}
                                            style={{ pointerEvents: 'none' }}>
                                            {`▶ ${viewLabel}`}
                                        </text>
                                    </g>
                                );
                            })
                        }

                        {/* Превью при рисовании */}
                        {previewElement && (<ElementRenderer element={previewElement} zoom={zoom} showConnectionPoints={false} />)}

                        {/* Прямоугольник выбора */}
                        {isSelecting && selectionBox && (
                            <rect x={Math.min(selectionBox.startX, selectionBox.endX)} y={Math.min(selectionBox.startY, selectionBox.endY)} width={Math.abs(selectionBox.endX - selectionBox.startX)} height={Math.abs(selectionBox.endY - selectionBox.startY)} fill="rgba(0,122,204,0.12)" stroke="#007ACC" strokeDasharray="4 4" />
                        )}
                    </svg>
                </div>
            </div>

            {ctxMenu.visible && (
                <WhiteboardContextMenu
                    x={ctxMenu.x}
                    y={ctxMenu.y}
                    items={finalContextMenuItems}
                    onClose={closeContextMenu}
                />
            )}

            {/* PropertyPanel для редактирования текста C4-элементов */}
            {c4EditState && (
                <PropertyPanel
                    x={c4EditState.x}
                    y={c4EditState.y}
                    sections={c4EditState.lines.map((line, i) => ({
                        id: `c4-line-${i}`,
                        label: c4EditState.lineLabels[i] || `Line ${i + 1}`,
                        type: 'text-input',
                        value: line,
                        icon: '',
                        placeholder: c4EditState.linePlaceholders[i] || '',
                        onChange: (val) => setC4EditState((prev) => {
                            if (!prev) return null;
                            const newLines = [...prev.lines];
                            newLines[i] = val;
                            return { ...prev, lines: newLines };
                        }),
                    }))}
                    onClose={() => {
                        if (c4EditState) {
                            const newContent = c4EditState.lines.join('\n');
                            handleTextEditCompleteWrapped(c4EditState.textId, { content: newContent });
                        }
                        setC4EditState(null);
                    }}
                />
            )}

            {/* BcmPropertyPanel для редактирования BCM-элементов */}
            {bcmEditState && (
                <BcmPropertyPanel
                    editState={bcmEditState}
                    onSave={handleBcmSave}
                    onClose={() => setBcmEditState(null)}
                />
            )}

            {/* Tooltip для системы при hover */}
            {systemTooltip.visible && systemTooltip.element && (
                <SystemTooltip
                    element={systemTooltip.element}
                    x={systemTooltip.x}
                    y={systemTooltip.y}
                />
            )}

            {/* Панель генерации кода */}
            {showCodePanel && ['uml', 'archimate', 'c4', 'erd', 'free'].includes(mode) && (
                <CodeGenerator
                    onGenerateElements={handleGenerateElementsWrapped}
                    existingElements={elements}
                    viewportCenter={viewportCenter}
                    topOffset={0}
                    hasToolbar={true}
                    fitBBoxInView={fitBBoxInView}
                    code={code}
                    setCode={setCode}
                    dslText={codePanelDsl || undefined}
                    onElementMapChange={mode === 'c4' ? (map) => { c4ElementMapRef.current = map; } : undefined}
                    onRegistryContextChange={handleRegistryContextChange}
                    onUserCodeChange={handleUserCodeChange}
                    diagramMode={mode}
                    metamodelContext={metamodelContext}
                    defaultCode={defaultCode}
                />
            )}
        </div>
        </BcmHeatmapContext.Provider>
    );
};

export default Canvas;
